import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { parentApi, parentAuth, formatApiErrorDetail } from "../lib/api";
import { NaaviLogo, ShapeFlower, ShapeRainbow } from "../components/brand/Shapes";
import { ArrowLeft, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function ParentLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/parent";
  const [step, setStep] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [resolvedEmail, setResolvedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestOtp = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await parentApi.post("/parent/request-otp", { identifier: identifier.trim() });
      setResolvedEmail(data.email);
      setMaskedEmail(data.masked_email || data.email);
      setStep("otp");
      toast.success("Code sent to your registered email.");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await parentApi.post("/parent/verify-otp", {
        email: resolvedEmail,
        otp: otp.trim(),
      });
      parentAuth.set(data.token);
      toast.success("Welcome to your Naavi parent portal.");
      navigate(next);
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] relative overflow-hidden flex items-center justify-center px-5 py-10" data-testid="parent-login-page">
      <ShapeFlower size={180} className="absolute -top-12 -right-10 opacity-80" color="#C6D947" />
      <ShapeRainbow size={140} className="absolute bottom-10 left-10 opacity-90" color="#5BC4B8" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#0F1B47] mb-6 font-semibold" data-testid="back-to-site">
          <ArrowLeft size={14} />
          Back to website
        </Link>

        <div className="bg-[#FDFDF9] rounded-[32px] p-8 md:p-10 shadow-[0_30px_80px_-30px_rgba(15,27,71,0.25)] border border-[#0F1B47]/8">
          <div className="flex items-center gap-3 mb-6">
            <NaaviLogo height={36} />
          </div>
          <div className="inline-flex items-center gap-2 bg-[#F5F5F0] rounded-full px-3 py-1.5 mb-5">
            <Mail size={13} className="text-[#648DE5]" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#0F1B47]">
              Parent portal
            </span>
          </div>

          {step === "email" ? (
            <>
              <h1 className="font-display text-3xl md:text-4xl font-medium text-[#0F1B47] leading-tight mb-2">
                Welcome back.
              </h1>
              <p className="text-[#4A5568] text-sm font-light mb-8">
                Enter the email or phone number registered with your child's school. We'll send a 6-digit code to your email.
              </p>
              <form onSubmit={requestOtp} className="space-y-4" data-testid="parent-email-form">
                <Field label="Email or phone">
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@email.com or 9876543210"
                    required
                    className={inputCls}
                    data-testid="parent-login-email"
                  />
                </Field>
                {error && (
                  <div className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="parent-login-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#0F1B47] text-[#FDFDF9] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] disabled:opacity-60"
                  data-testid="parent-login-request-otp"
                >
                  {loading ? "Sending code…" : "Send sign-in code"}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl md:text-4xl font-medium text-[#0F1B47] leading-tight mb-2">
                Check your inbox.
              </h1>
              <p className="text-[#4A5568] text-sm font-light mb-8">
                Enter the 6-digit code we just sent to <b>{maskedEmail}</b>. Valid for 10 minutes.
              </p>
              <form onSubmit={verifyOtp} className="space-y-4" data-testid="parent-otp-form">
                <Field label="Sign-in code">
                  <div className="relative">
                    <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0F1B47]/50" />
                    <input
                      inputMode="numeric"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      placeholder="••••••"
                      required
                      className={`${inputCls} pl-11 tracking-[0.5em] text-lg`}
                      data-testid="parent-login-otp"
                    />
                  </div>
                </Field>
                {error && (
                  <div className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="parent-login-error">
                    {error}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => { setStep("email"); setOtp(""); setError(""); }}
                    className="bg-transparent border-2 border-[#0F1B47] text-[#0F1B47] rounded-full px-6 py-3 font-semibold text-sm hover:bg-[#0F1B47] hover:text-[#FDFDF9]"
                    data-testid="parent-login-change-email"
                  >
                    Change details
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-3 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-60"
                    data-testid="parent-login-verify"
                  >
                    {loading ? "Verifying…" : "Verify & sign in"}
                  </button>
                </div>
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-[#0F1B47]/8">
            <p className="text-xs text-[#718096] font-light">
              Are you Naavi staff?{" "}
              <Link to="/admin/login" className="font-semibold text-[#0F1B47] underline underline-offset-2">
                Use the admin console →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-5 py-3.5 text-[15px] text-[#0F1B47] placeholder:text-[#718096] font-medium focus:outline-none focus:border-[#648DE5] focus:ring-4 focus:ring-[#648DE5]/10 transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}
